import React, { useMemo, useState } from "react"

import { Clock, Layers, MapPin, Search, Users } from "lucide-react"

import { LoadingState } from "@/components/shared/LoadingState"
import { PageHeader } from "@/components/shared/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useCustomers, useLocations, useSlots } from "@/hooks/useCollections"
import { CoverageFilter, NO_COVERAGE_FILTER } from "@/lib/collectionCoverage"
import { Customer, Location } from "@/types/collections"

import SiteSlotsDialog from "./SiteSlotsDialog"
import BulkCreateDialog from "./directory/BulkCreateDialog"
import CustomerDialog from "./directory/CustomerDialog"
import CustomersTab from "./directory/CustomersTab"
import LocationDialog from "./directory/LocationDialog"
import LocationsTab from "./directory/LocationsTab"
import SlotsTab from "./directory/SlotsTab"
import { DirectoryTab, LocationsView } from "./directory/types"
import { useBulkCreate } from "./directory/useBulkCreate"
import { useCustomerDialog } from "./directory/useCustomerDialog"
import { useLocationDialog } from "./directory/useLocationDialog"

const DirectoryView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<DirectoryTab>("customers")
  const [locationsView, setLocationsView] = useState<LocationsView>("list")
  const [coverageFilter, setCoverageFilter] = useState<CoverageFilter>(NO_COVERAGE_FILTER)
  const [searchTerm, setSearchTerm] = useState("")

  const {
    data: customersData,
    isLoading: customersLoading,
    refetch: refetchCustomers,
  } = useCustomers()
  const {
    data: locationsData,
    isLoading: locationsLoading,
    refetch: refetchLocations,
  } = useLocations()
  const { data: slotsData, isLoading: slotsLoading, refetch: refetchSlots } = useSlots()

  // Memoised: the `|| []` fallback would otherwise defeat every downstream useMemo.
  const customers = useMemo(() => customersData?.data || [], [customersData])
  const locations = useMemo(() => locationsData?.data || [], [locationsData])
  const slots = useMemo(() => slotsData?.data || [], [slotsData])

  const customerDialog = useCustomerDialog(refetchCustomers)
  const locationDialog = useLocationDialog(customers, refetchLocations)
  const bulk = useBulkCreate(locations, { refetchCustomers, refetchLocations, refetchSlots })

  const [siteEditorLocation, setSiteEditorLocation] = useState<Location | null>(null)
  const openShipper = (customer: Customer) => {
    const sites = getLocationsByCustomerId(customer.id)
    if (sites.length > 0) setSiteEditorLocation(sites[0])
  }

  const revealCustomer = (customerId: string) => {
    const customer = getCustomerById(customerId)
    if (customer) openShipper(customer)
  }

  // Primary sites plus those linked through the junction table.
  const getLocationsByCustomerId = (customerId: string): Location[] => {
    return locations.filter(
      (l) =>
        l.customer_id === customerId ||
        (l.customers?.some((lc) => lc.customer_id === customerId) ?? false),
    )
  }

  const getCustomerById = (customerId: string): Customer | undefined => {
    return customers.find((c) => c.id === customerId)
  }

  const filteredCustomers = useMemo(() => {
    if (!searchTerm) return customers
    const term = searchTerm.toLowerCase()
    return customers.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.account_id.toLowerCase().includes(term) ||
        c.customer_type.toLowerCase().includes(term),
    )
  }, [customers, searchTerm])

  const filteredLocations = useMemo(() => {
    if (!searchTerm) return locations
    const term = searchTerm.toLowerCase()
    return locations.filter(
      (l) =>
        l.site_name.toLowerCase().includes(term) ||
        l.location_id.toLowerCase().includes(term) ||
        l.address.toLowerCase().includes(term) ||
        l.postcode.toLowerCase().includes(term),
    )
  }, [locations, searchTerm])

  const customersById = useMemo(
    () => new globalThis.Map(customers.map((c) => [c.id, c])),
    [customers],
  )

  const filteredSlots = useMemo(() => {
    if (!searchTerm) return slots
    const term = searchTerm.toLowerCase()
    return slots.filter((s) => {
      const location = locations.find((l) => l.id === s.location_id)
      const days = Array.isArray(s.days) ? s.days.join(" ") : ""
      return (
        (s.slot_name?.toLowerCase().includes(term) ?? false) ||
        days.toLowerCase().includes(term) ||
        (location?.site_name.toLowerCase().includes(term) ?? false)
      )
    })
  }, [slots, searchTerm, locations])

  const isLoading = customersLoading || locationsLoading || slotsLoading

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-palette-jungleGreen000">
      <PageHeader
        title="Directory"
        subtitle="Manage customers, locations, and collection slots"
        actions={
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-palette-jungleGreen400" />
            <Input
              placeholder="Search directory..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-palette-jungleGreen000 border-palette-jungleGreen200 h-9 text-sm"
            />
          </div>
        }
      />

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as DirectoryTab)}
        className="flex-1 flex flex-col min-h-0"
      >
        <div className="bg-white border-b border-palette-jungleGreen200 px-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <TabsList className="bg-transparent h-12 p-0 gap-6">
              <TabsTrigger
                value="customers"
                className="data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:border-b-2 data-[state=active]:border-blue-600 text-palette-jungleGreen500 rounded-none px-0 pb-3 h-full gap-2"
              >
                <Users size={16} />
                Customers
                <Badge
                  variant="secondary"
                  className="ml-1 bg-palette-jungleGreen100 text-palette-jungleGreen600 pointer-events-none"
                >
                  {isLoading ? "..." : customers.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger
                value="locations"
                className="data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:border-b-2 data-[state=active]:border-blue-600 text-palette-jungleGreen500 rounded-none px-0 pb-3 h-full gap-2"
              >
                <MapPin size={16} />
                Locations
                <Badge
                  variant="secondary"
                  className="ml-1 bg-palette-jungleGreen100 text-palette-jungleGreen600 pointer-events-none"
                >
                  {isLoading ? "..." : locations.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger
                value="slots"
                className="data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:border-b-2 data-[state=active]:border-blue-600 text-palette-jungleGreen500 rounded-none px-0 pb-3 h-full gap-2"
              >
                <Clock size={16} />
                Slots
                <Badge
                  variant="secondary"
                  className="ml-1 bg-palette-jungleGreen100 text-palette-jungleGreen600 pointer-events-none"
                >
                  {isLoading ? "..." : slots.length}
                </Badge>
              </TabsTrigger>
            </TabsList>
            <Button
              variant="outline"
              size="sm"
              onClick={bulk.openDialog}
              className="h-8 bg-white border-palette-jungleGreen200 text-palette-jungleGreen600 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 gap-1.5 text-xs"
            >
              <Layers size={14} />
              Bulk Create
            </Button>
          </div>
        </div>

        {isLoading && <LoadingState label="Loading data..." className="flex-1" />}

        {!isLoading && (
          <div className="flex-1 overflow-auto p-4">
            <CustomersTab
              customers={filteredCustomers}
              locationsFor={getLocationsByCustomerId}
              onOpenShipper={openShipper}
              onNew={() => customerDialog.openDialog()}
              onEdit={customerDialog.openDialog}
            />

            <LocationsTab
              locations={filteredLocations}
              slots={slots}
              customersById={customersById}
              coverageFilter={coverageFilter}
              onCoverageFilterChange={setCoverageFilter}
              view={locationsView}
              onViewChange={setLocationsView}
              onNew={() => locationDialog.openDialog()}
              onEdit={locationDialog.openDialog}
              onOpenSite={setSiteEditorLocation}
              onRevealCustomer={revealCustomer}
            />

            <SlotsTab
              slots={filteredSlots}
              locations={locations}
              onOpenSite={setSiteEditorLocation}
            />
          </div>
        )}
      </Tabs>

      <CustomerDialog dialog={customerDialog} />

      <LocationDialog dialog={locationDialog} />

      <SiteSlotsDialog
        open={siteEditorLocation !== null}
        onOpenChange={(open) => !open && setSiteEditorLocation(null)}
        location={siteEditorLocation}
        sites={
          siteEditorLocation?.customer_id
            ? getLocationsByCustomerId(siteEditorLocation.customer_id)
            : []
        }
        onSelectSite={setSiteEditorLocation}
        customer={
          siteEditorLocation?.customer_id
            ? customersById.get(siteEditorLocation.customer_id)
            : undefined
        }
        slots={slots}
        onSaved={() => {
          refetchSlots()
          refetchLocations()
        }}
      />

      <BulkCreateDialog bulk={bulk} />
    </div>
  )
}

export default DirectoryView
